import { Box, Button, Typography, Alert, TextField } from "@mui/material";
import { standaloneFieldSx } from "../../common/fieldStyles";
import RecurringServicesDialog from "../../component/vehicle/RecurringServicesDialog";
import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import SelectorClient from "./selectorClient";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { httpClient } from "../../common/httpclient";
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NotesIcon from '@mui/icons-material/Notes';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import Build from '@mui/icons-material/Build';
import Speed from '@mui/icons-material/Speed';
import { Stack } from '@mui/material';
import TextAreaWithCounter from '../../component/input/TextAreaWithCounter';
import { Check } from 'lucide-react';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { useEnterpriseConfig } from '../../component/context/EnterpriseConfigContext';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';

dayjs.extend(utc);

// `ff-textarea`/campos com `TextField` outlined padrão (DESIGN-SYSTEM.md
// §5.5 A "Floating label") — mesma cópia local do padrão já estabelecido em
// `pages/products/Form.js` (`standaloneFieldSx`/`standaloneInputBoxSx`) e em
// `selectorClient.js` (Cliente/Veículo), usada aqui só no campo Observações.
const ffFieldSx = {
    '& .MuiInputBase-input': { fontSize: '13.5px' },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: '96px',
            alignItems: 'flex-start',
            padding: '24px 14px 7px 14px',
        },
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiInputBase-multiline textarea': {
        resize: 'vertical',
    },
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
};

// `DateTimePicker` (MUI X) usa um namespace de classes próprio
// (`MuiPickersOutlinedInput-*`/`MuiPickersSectionList-*`), diferente de
// `MuiOutlinedInput-*` de um `TextField` comum — os `styleOverrides`
// genéricos do tema (e o `ffFieldSx` acima) não o alcançam. Mesmo tamanho
// `ff-input` (52px, radius 12px, fundo `background.subtle`) dos demais
// campos deste formulário.
const ffPickerSx = {
    // Label do `.ff-label`: repouso 13.5px, encolhido 10px/800 uppercase
    // dentro da caixa (sem isso o `MuiInputLabel` cai no default do tema, que
    // cruza a borda porque o `legend` é removido abaixo).
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
    '& .MuiPickersOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        height: '52px',
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiPickersOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
        fontSize: '13.5px',
        // Mesmo `padding: 21px 14px 7px` do `<input>` do `ff-input` (o container
        // de seções é centralizado na caixa de 52px, então o valor precisa da
        // mesma reserva no topo para não encostar no label encolhido). O padrão
        // do MUI X é `16.5px 0`, que deixava o valor sobre o label.
        padding: '21px 0 7px',
    },
};

// `<span class="ff-req">*</span>` do protótipo — asterisco de campo
// obrigatório na cor de erro/danger.
const requiredFieldLabel = (text) => (
    <>
        {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </>
);

const OrderData = forwardRef(({
    isForm, 
    editMode, 
    handleSubmit, 
    id, 
    setOrderNumber, 
    wasUpdated, 
    setOrderStatus, 
    onValidationChange,
    showActionButtons = false,
    onSave,
    isFormValid = false,
    isSaving = false,
    status,
    onStatusChange, // Nova prop para callback de mudança de status
    onOrderDataChange, // Nova prop para callback de mudança de dados da ordem
}, ref) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [edit, setEdit] = useState(isForm);
    const [order, setOrder] = useState(null);
    const [currentStatus, setCurrentStatus] = useState(status || 'Não Iniciada'); // Estado local para o status
    const storage = localStorage;
    const [useVehicle, setUseVehicle] = useState(false);
    const { settings, isLoading: configLoading } = useEnterpriseConfigContext();
    
    // Helper function para garantir que o valor seja sempre uma instância dayjs válida e editável
    const normalizeDateValue = (dateValue) => {
        if (!dateValue) return null;
        if (typeof dateValue.isValid === 'function' && dateValue.isValid()) {
            // Se já é uma instância dayjs válida, retornar uma nova instância para garantir mutabilidade
            return dayjs(dateValue);
        }
        // Tentar converter para dayjs
        const date = dayjs(dateValue);
        return date.isValid() ? date : null;
    };
    
    // Valores iniciais estáveis para as datas
    const initialDateValues = useMemo(() => ({
        order_date: dayjs(),
        delivery_date: dayjs()
    }), []);
    
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            customer_id: Yup.string().required('Cliente é obrigatório'),
            order_date: Yup.mixed().nullable().required('Data é obrigatória').test('is-valid-date', 'Data inválida', (value) => {
                if (!value) return false;
                const date = value && typeof value.isValid === 'function' ? value : dayjs(value);
                return date && date.isValid && date.isValid();
            }),
            delivery_date: Yup.mixed().nullable().required('Data de entrega é obrigatória').test('is-valid-date', 'Data inválida', (value) => {
                if (!value) return false;
                const date = value && typeof value.isValid === 'function' ? value : dayjs(value);
                return date && date.isValid && date.isValid();
            }),
            observations: Yup.string().optional(),
            defect_reason: Yup.string().optional()
        });
    }, [settings?.os_use_vehicle]);

    const formik = useFormik({
        initialValues: {
            customer_id: '',
            order_date: initialDateValues.order_date,
            delivery_date: initialDateValues.delivery_date,
            observations: '',
            defect_reason: '',
            vehicle_id: ''
        },
        validateOnMount: false,
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: validationSchema,
        onSubmit: (values) => {           
            // Formatar as datas para o formato correto (YYYY-MM-DDTHH:mm) em UTC
            const formatDate = (dateValue) => {
                if (!dateValue) return '';
                // Se for dayjs, usar diretamente, senão converter
                const date = dateValue && typeof dateValue.isValid === 'function' ? dateValue : dayjs(dateValue);
                if (!date || !date.isValid || !date.isValid()) return '';
                // Converter para UTC mantendo os mesmos valores de data/hora (não o mesmo instante)
                const utcDate = dayjs.utc(date.format('YYYY-MM-DD HH:mm'));
                return utcDate.format('YYYY-MM-DDTHH:mm');
            };

            const formattedValues = {
                ...values,
                order_date: formatDate(values.order_date),
                delivery_date: formatDate(values.delivery_date)
            };
            
            handleSubmit(formattedValues);
                       
        },
        enableReinitialize: true
    });

    useEffect(() => {
        if (settings && settings.os_use_vehicle) {
            setUseVehicle(true);
        } else if (settings && !settings.os_use_vehicle) {
            setUseVehicle(false);
        }
    }, [settings]);

    useEffect(() => {
        if (isForm) {
            setEdit(true);
        }
        if (wasUpdated) {
            fetchOrderData();
        }
    }, [isForm, wasUpdated]);

    // Forçar revalidação quando useVehicle mudar
    useEffect(() => {
        if (formik && settings) {
            formik.validateForm();
        }
    }, [settings?.os_use_vehicle]);

    // Notificar mudanças de validação
    useEffect(() => {
        if (onValidationChange) {
            // Verificar se todos os campos obrigatórios estão preenchidos
            const hasCustomer = formik.values.customer_id && formik.values.customer_id.toString().trim() !== '';
            const orderDate = formik.values.order_date;
            const hasOrderDate = orderDate && (orderDate.isValid ? orderDate.isValid() : dayjs(orderDate).isValid());
            const deliveryDate = formik.values.delivery_date;
            const hasDeliveryDate = deliveryDate && (deliveryDate.isValid ? deliveryDate.isValid() : dayjs(deliveryDate).isValid());
            
            const hasAllRequiredFields = hasCustomer && hasOrderDate && hasDeliveryDate;
            
            const hasNoErrors = Object.keys(formik.errors).length === 0;
            const hasTouchedFields = Object.keys(formik.touched).length > 0;
            
            const isValid = hasAllRequiredFields && hasNoErrors && hasTouchedFields;
            
            onValidationChange(isValid);
        }
    }, [formik.values, formik.errors, formik.touched, useVehicle, onValidationChange]);

    const fetchOrderData = async () => {
        try {
            const response = await httpClient.get(`/orderservices/${id}`);
            ;
 
            console.log(response.data);
            if (response.status === 200) {
                setOrder(response.data.order);
                
                setOrderNumber(response.data.order?.number);
                
                // Definir o status primeiro
                const newStatus = response.data.order?.status || 'Não Iniciada';
                
                // Só atualizar se o status realmente mudou
                if (newStatus !== currentStatus) {
                    setCurrentStatus(newStatus);
                    // Notificar componente pai apenas se o status mudou
                    setOrderStatus(newStatus);
                    
                    // Chamar callback de mudança de status se fornecido
                    if (onStatusChange) {
                        onStatusChange(newStatus);
                    }
                }
                
                // Notificar componente pai sobre mudanças nos dados da ordem
                if (onOrderDataChange) {
                    onOrderDataChange(response.data.order);
                }
                
                setSelectedCustomer(response.data.order?.customer_id || '');
                setSelectedVehicle(response.data?.order.vehicle_id || '');
                formik.setValues({
                    customer_id: response.data.order?.customer_id || '',
                    order_date: response.data.order?.order_date ? dayjs(response.data.order.order_date) : null,
                    delivery_date: response.data.order?.delivery_date ? dayjs(response.data.order.delivery_date) : null,
                    observations: response.data.order?.observations || '',
                    defect_reason: response.data.order?.defect_reason || '',
                    vehicle_id: response.data.order?.vehicle_id || ''
                });
            }
        } catch (error) {
            console.error('Error fetching order data:', error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchOrderData();            
        }
    }, [id]);

    // Sincronizar status quando a prop status mudar
    useEffect(() => {
        if (status && status !== currentStatus) {
            setCurrentStatus(status);
        }
    }, [status]); // Removida dependência currentStatus para evitar loop

    useImperativeHandle(ref, () => ({
        submitForm: () => {
            // Forçar validação antes de submeter
            formik.validateForm().then((errors) => {
                if (Object.keys(errors).length === 0) {
                    formik.handleSubmit();
                } else {
                    // Marcar todos os campos como touched para mostrar os erros
                    const touchedFields = {};
                    Object.keys(formik.values).forEach(key => {
                        touchedFields[key] = true;
                    });
                    formik.setTouched(touchedFields);
                }
            });
        },
        // Expor o status atual para o componente pai
        getCurrentStatus: () => currentStatus,
        // Expor dados da ordem para o componente pai
        getOrderData: () => order,
        // Método para atualizar status manualmente
        updateStatus: (newStatus) => {
            if (newStatus && newStatus !== currentStatus) {
                setCurrentStatus(newStatus);
                setOrderStatus(newStatus);
                if (onStatusChange) {
                    onStatusChange(newStatus);
                }
            }
        }
    }));

    const handleCustomerSelect = (customerId) => {
        formik.setFieldValue('customer_id', customerId);
        formik.setFieldTouched('customer_id', true);
        setSelectedCustomer(customerId);
        
        // Forçar revalidação após um pequeno delay para garantir que o valor foi atualizado
        setTimeout(() => {
            formik.validateField('customer_id');
        }, 100);
    };

    const handleVehicleSelect = (event) => {
        ;
        const vehicleId = event.target.value;
        formik.setFieldValue('vehicle_id', vehicleId);
        formik.setFieldTouched('vehicle_id', true);
        setSelectedVehicle(vehicleId);
        
      
    };

    // "KM atual (consulta)": só simula o alerta de serviços recorrentes com o km
    // digitado (?kilometer=). NÃO entra no formik/payload e não grava nada
    // (nem em service_orders.current_kilometer nem em vehicles.kilometer).
    // Debounce de 600ms, uma consulta por combinação veículo+km, falha não bloqueia.
    const [consultKm, setConsultKm] = useState('');
    const [recurringDue, setRecurringDue] = useState(null);
    const [openRecurringDialog, setOpenRecurringDialog] = useState(false);
    const lastCheckedKmKeyRef = useRef('');
    const consultVehicleId = formik.values.vehicle_id;
    useEffect(() => {
        if (!useVehicle || !consultVehicleId) return;
        const kmRaw = consultKm.trim();
        if (!/^\d+$/.test(kmRaw)) return;
        const km = Number(kmRaw);
        const key = `${consultVehicleId}|${km}`;
        if (lastCheckedKmKeyRef.current === key) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            lastCheckedKmKeyRef.current = key;
            httpClient.get(`/vehicles/${consultVehicleId}/recurring-services/due`, { params: { kilometer: km } })
                .then((response) => {
                    if (cancelled) return;
                    const data = response.data;
                    if (data && Array.isArray(data.items) && data.items.length > 0) {
                        setRecurringDue(data);
                        setOpenRecurringDialog(true);
                    }
                })
                .catch((error) => {
                    console.error(`Erro ao verificar serviços recorrentes do veículo ${consultVehicleId} para o KM ${km}:`, error);
                });
        }, 600);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [useVehicle, consultVehicleId, consultKm]);

    // Mostrar loading enquanto as configurações estão sendo carregadas
    if (configLoading) {
        return (
            <Box sx={{ width: '100%', p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Carregando configurações...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {isForm ? (
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ p: 2 }}>
                        <Stack spacing={3}>
                            {/* Datas e Horários — dois campos lado a lado, `ff-input`
                                (§5.5 A), igual a `prototipo-ordem-servico.html` `view-nova`. */}
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                                    <DateTimePicker
                                        label={requiredFieldLabel('Data e hora da ordem')}
                                        format="DD/MM/YYYY HH:mm"
                                        value={normalizeDateValue(formik.values.order_date)}
                                        onChange={(newValue) => {
                                            formik.setFieldValue('order_date', newValue);
                                            formik.setFieldTouched('order_date', true);
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: "outlined",
                                                size: "medium",
                                                fullWidth: true,
                                                error: formik.touched.order_date && Boolean(formik.errors.order_date),
                                                helperText: formik.touched.order_date && formik.errors.order_date,
                                                sx: ffPickerSx,
                                            }
                                        }}
                                    />

                                    <DateTimePicker
                                        label={requiredFieldLabel('Data e hora de entrega')}
                                        format="DD/MM/YYYY HH:mm"
                                        value={normalizeDateValue(formik.values.delivery_date)}
                                        onChange={(newValue) => {
                                            formik.setFieldValue('delivery_date', newValue);
                                            formik.setFieldTouched('delivery_date', true);
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: "outlined",
                                                size: "medium",
                                                fullWidth: true,
                                                error: formik.touched.delivery_date && Boolean(formik.errors.delivery_date),
                                                helperText: formik.touched.delivery_date && formik.errors.delivery_date,
                                                sx: ffPickerSx,
                                            }
                                        }}
                                    />
                                </Box>
                            </LocalizationProvider>

                            {/* Cliente + veículo — combobox de busca-e-anexar / select
                                de rótulo estático (§5.5), delegado a `SelectorClient`. */}
                            <SelectorClient
                                formik={formik}
                                selectedCustomer={selectedCustomer}
                                handleChange={handleCustomerSelect}
                                selectedVehicle={selectedVehicle}
                                handleVehicleChange={handleVehicleSelect}
                                useVehicle={useVehicle}
                            />

                            {/* KM atual (somente consulta de alerta) — não é enviado no payload */}
                            {useVehicle && formik.values.vehicle_id && (
                                <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                                    <TextField
                                        label="KM atual (consulta)"
                                        name="consult_kilometer"
                                        value={consultKm}
                                        onChange={(e) => setConsultKm(e.target.value.replace(/\D/g, ''))}
                                        helperText="Apenas para consultar alertas de serviços recorrentes. Não altera o KM do veículo."
                                        inputProps={{ inputMode: 'numeric' }}
                                        variant="outlined"
                                        size="medium"
                                        fullWidth
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            )}
                            <RecurringServicesDialog
                                open={openRecurringDialog}
                                onClose={() => setOpenRecurringDialog(false)}
                                data={recurringDue}
                            />

                            {/* Motivo/Defeito — `ff-textarea` (§5.5 A) */}
                            <TextAreaWithCounter
                                label="Motivo/Defeito (opcional)"
                                maxLength={500}
                                name="defect_reason"
                                value={formik.values.defect_reason}
                                handleChange={formik.handleChange}
                                error={formik.touched.defect_reason && Boolean(formik.errors.defect_reason)}
                                helperText={formik.touched.defect_reason && formik.errors.defect_reason}
                                variant="outlined"
                                rows={3}
                                sx={ffFieldSx}
                                captionText="Descreva o motivo da entrada ou o defeito relatado"
                            />

                            {/* Observações — `ff-textarea` (§5.5 A) */}
                            <TextAreaWithCounter
                                label="Observações (opcional)"
                                maxLength={500}
                                name="observations"
                                value={formik.values.observations}
                                handleChange={formik.handleChange}
                                error={formik.touched.observations && Boolean(formik.errors.observations)}
                                helperText={formik.touched.observations && formik.errors.observations}
                                variant="outlined"
                                rows={3}
                                sx={ffFieldSx}
                            />
                        </Stack>

                        {/* Validation Summary */}
                        {!formik.isValid && formik.dirty && (
                            <Box sx={{ mt: 3 }}>
                                <Alert
                                    severity="warning"
                                    icon={<CheckCircleIcon />}
                                >
                                    <Typography variant="body2">
                                        Por favor, corrija os erros no formulário antes de continuar.
                                    </Typography>
                                </Alert>
                            </Box>
                        )}
                    </Box>

                    {/* Action Buttons */}
                    {showActionButtons && (
                        <Box sx={{
                            p: 2,
                            backgroundColor: theme.palette.background.paper,
                            borderTop: '1px solid',
                            borderColor: theme.palette.divider
                        }}>
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/serviceorders')}
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<Check size={15} strokeWidth={2.5} />}
                                    onClick={onSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Gerando ordem...' : 'Gerar ordem'}
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </form>
            ) : (
                <Box sx={{ 
                    p: 3,
                    background: theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, rgba(18, 18, 18, 0.8) 0%, rgba(25, 25, 25, 0.9) 100%)'
                        : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.9) 100%)',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                    boxShadow: theme.palette.mode === 'dark' 
                        ? '0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)'
                        : '0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    {/* Header com Status e Número da Ordem */}
                   

                    {/* Grid Principal de Informações */}
                    <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: { 
                            xs: '1fr', 
                            sm: order?.vehicle_id ? 'repeat(2, 1fr)' : '1fr', 
                            lg: order?.vehicle_id ? 'repeat(2, 1fr)' : '1fr' 
                        },
                        gap: 3,
                        mb: 4
                    }}>
                        {/* Card de Datas e Horários */}
                        <Paper sx={{ 
                            p: 3,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(30, 136, 229, 0.12) 100%)'
                                : 'linear-gradient(135deg, rgba(33, 150, 243, 0.03) 0%, rgba(30, 136, 229, 0.06) 100%)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)'}`,
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <Box sx={{ 
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 60,
                                height: 60,
                                background: theme.palette.mode === 'dark' 
                                    ? 'radial-gradient(circle, rgba(33, 150, 243, 0.1) 0%, transparent 70%)'
                                    : 'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, transparent 70%)',
                                borderRadius: '50%'
                            }} />
                            
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                <Box sx={{ 
                                    p: 1,
                                    borderRadius: 1.5,
                                    background: theme.palette.mode === 'dark' 
                                        ? 'rgba(33, 150, 243, 0.2)'
                                        : 'rgba(33, 150, 243, 0.1)'
                                }}>
                                    <ArticleIcon sx={{ fontSize: 20, color: 'info.main' }} />
                                </Box>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 600,
                                    color: 'text.primary'
                                }}>
                                    Detalhes da Ordem
                                </Typography>
                            </Stack>
                            
                            <Stack spacing={2}>
                                {/* Layout com datas lado a lado e cliente abaixo */}
                                <Box sx={{ 
                                    display: 'grid',
                                    gridTemplateColumns: { 
                                        xs: '1fr', 
                                        sm: order?.vehicle_id ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
                                        lg: order?.vehicle_id ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)'
                                    },
                                    gap: 3,
                                    mb: order?.vehicle_id ? 0 : 2
                                }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            mb: 0.5,
                                            display: 'block'
                                        }}>
                                            Data do Pedido
                                        </Typography>
                                        <Typography variant="body1" sx={{ 
                                            fontWeight: 500,
                                            color: 'text.primary'
                                        }}>
                                            {order?.order_date && new Date(order.order_date).toLocaleString('pt-BR', { 
                                                timeZone: 'UTC',
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </Typography>
                                    </Box>
                                    
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            mb: 0.5,
                                            display: 'block'
                                        }}>
                                            Data de Entrega
                                        </Typography>
                                        <Typography variant="body1" sx={{ 
                                            fontWeight: 500,
                                            color: 'text.primary'
                                        }}>
                                            {order?.delivery_date && new Date(order.delivery_date).toLocaleString('pt-BR', { 
                                                timeZone: 'UTC',
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                {/* Cliente sempre em linha separada */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ 
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        mb: 0.5,
                                        display: 'block'
                                    }}>
                                        Cliente
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary'
                                    }}>
                                        {order?.customer_name}
                                    </Typography>
                                </Box>
                                
                                {/* Orçamento (se existir) */}
                                {order?.budget_id && order?.budget_number && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            mb: 0.5,
                                            display: 'block'
                                        }}>
                                            Orçamento
                                        </Typography>
                                        <Link 
                                            to={`/servicequotes/${order.budget_id}/view`}
                                            style={{ 
                                                textDecoration: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8
                                            }}
                                        >
                                            <DescriptionIcon sx={{ 
                                                fontSize: 18, 
                                                color: 'primary.main' 
                                            }} />
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    fontWeight: 500,
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                #{order.budget_number}
                                            </Typography>
                                        </Link>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>

                        

                        {/* Card de Veículo (se existir) */}
                        {order?.vehicle_id && (
                            <Paper sx={{ 
                                p: 3,
                                background: theme.palette.mode === 'dark' 
                                    ? 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(142, 36, 170, 0.12) 100%)'
                                    : 'linear-gradient(135deg, rgba(156, 39, 176, 0.03) 0%, rgba(142, 36, 170, 0.06) 100%)',
                                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)'}`,
                                borderRadius: 2,
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{ 
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    width: 60,
                                    height: 60,
                                    background: theme.palette.mode === 'dark' 
                                        ? 'radial-gradient(circle, rgba(156, 39, 176, 0.1) 0%, transparent 70%)'
                                        : 'radial-gradient(circle, rgba(156, 39, 176, 0.05) 0%, transparent 70%)',
                                    borderRadius: '50%'
                                }} />
                                
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                    <Box sx={{ 
                                        p: 1,
                                        borderRadius: 1.5,
                                        background: theme.palette.mode === 'dark' 
                                            ? 'rgba(156, 39, 176, 0.2)'
                                            : 'rgba(156, 39, 176, 0.1)'
                                    }}>
                                        {order?.vehicle_type === 'bicycle' ? <PedalBikeIcon sx={{ fontSize: 20, color: 'secondary.main' }} /> :
                                         order?.vehicle_type === 'motorcycle' ? <TwoWheelerIcon sx={{ fontSize: 20, color: 'secondary.main' }} /> :
                                         <DirectionsCarIcon sx={{ fontSize: 20, color: 'secondary.main' }} />}
                                    </Box>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 600,
                                        color: 'text.primary'
                                    }}>
                                        Veículo
                                    </Typography>
                                </Stack>
                                
                                <Stack spacing={1.5}>
                                    <Typography variant="body1" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '1.1rem'
                                    }}>
                                        {order?.vehicle_name || '-'}
                                    </Typography>
                                    
                                    {order?.vehicle_type !== 'bicycle' && order?.vehicle_plate && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            background: theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(0,0,0,0.02)'
                                        }}>
                                            <ConfirmationNumber sx={{ fontSize: 16, color: 'success.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.vehicle_plate}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {order?.vehicle_chassi && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            background: theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(0,0,0,0.02)'
                                        }}>
                                            <Build sx={{ fontSize: 16, color: 'info.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.vehicle_chassi}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {order?.vehicle_kilometer && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            background: theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(0,0,0,0.02)'
                                        }}>
                                            <Speed sx={{ fontSize: 16, color: 'warning.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.vehicle_kilometer} km
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Box>

                    {/* Card de Observações */}
                    {(order?.observations || order?.defect_reason) && (
                        <Paper sx={{ 
                            p: 3,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(245, 124, 0, 0.12) 100%)'
                                : 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(245, 124, 0, 0.06) 100%)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)'}`,
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <Box sx={{ 
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 60,
                                height: 60,
                                background: theme.palette.mode === 'dark' 
                                    ? 'radial-gradient(circle, rgba(255, 152, 0, 0.1) 0%, transparent 70%)'
                                    : 'radial-gradient(circle, rgba(255, 152, 0, 0.05) 0%, transparent 70%)',
                                borderRadius: '50%'
                            }} />
                            
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                <Box sx={{ 
                                    p: 1,
                                    borderRadius: 1.5,
                                    background: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 152, 0, 0.2)'
                                        : 'rgba(255, 152, 0, 0.1)'
                                }}>
                                    <NotesIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 600,
                                    color: 'text.primary'
                                }}>
                                    Observações
                                </Typography>
                            </Stack>

                            {order.defect_reason && (
                                <Box sx={{ mb: order.observations ? 2 : 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
                                        Motivo/Defeito
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        fontWeight: 400,
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6,
                                        color: 'text.primary',
                                        p: 2,
                                        borderRadius: 1,
                                        background: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(0,0,0,0.02)',
                                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
                                    }}>
                                        {order.defect_reason}
                                    </Typography>
                                </Box>
                            )}

                            {order.observations && (
                                <Box>
                                    {order.defect_reason && (
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
                                            Observações
                                        </Typography>
                                    )}
                                    <Typography variant="body1" sx={{
                                        fontWeight: 400,
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6,
                                        color: 'text.primary',
                                        p: 2,
                                        borderRadius: 1,
                                        background: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(0,0,0,0.02)',
                                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
                                    }}>
                                        {order.observations}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default OrderData;